import React, { memo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import Box from 'ui-box';
import {
  Button,
  Container,
  Heading,
  Icon,
  majorScale,
  minorScale,
  PageContainer,
  PageLoader,
  Tag,
  VirtualTable,
  VirtualTableItem,
} from '../../npaper';
import { CompanyThumbnail } from './CompanyThumbnail';
import {
  Company,
  ICompany,
  ICompanySnapshotOut,
  PaymentLikelihoodType,
  useStores,
} from '../../stores';
import { CompanyDetailsDialog } from './CompanyDetailsDialog';
import { useHistory } from 'react-router-dom';
import { PaymentLikelihoodTag } from './PaymentLikelihoodTag';
import { useFilterFetch } from '../../stores/utils/useFilterFetch';
import { observer } from 'mobx-react-lite';
import { FilterPopover } from '../../npaper/FilterPopover';
import { SearchInput } from '../../npaper/SearchInput';

export const Companies = () => {
  const { companyStore } = useStores();
  const {
    items,
    isLoading,
    fetchMore,
    total,
    setFilter,
    filter,
  } = useFilterFetch<typeof Company>(companyStore as any, {
    ordering: 'name,id',
  });
  const history = useHistory();

  const [isCreatingCompany, setIsCreatingCompany] = useState(false);

  return !items ? (
    <PageContainer>
      <PageLoader />
    </PageContainer>
  ) : (
    <PageContainer
      display="flex"
      flexDirection="column"
      paddingBottom="0"
      height="100%"
      maxHeight="100vh"
    >
      <CompanyDetailsDialog
        isShown={isCreatingCompany}
        onClose={(company) => {
          setIsCreatingCompany(false);
          if (company) {
            history.push(`/companies/${company.id}`);
          }
        }}
      />
      <Container marginBottom={majorScale(2)} flexShrink={0} flexGrow={0}>
        <Heading size={700} marginBottom={minorScale(3)}>
          Virksomheder
        </Heading>
        <Box display="flex" justifyContent="space-between">
          <SearchInput
            marginRight={majorScale(1)}
            currentQuery={filter.filter ? filter.filter.search : ''}
            onSearch={(query) => {
              setFilter({
                ...filter,
                filter: { ...filter.filter, search: query },
              });
            }}
          />
          <FilterPopover<ICompanySnapshotOut>
            filter={filter.filter}
            onChange={(f) => setFilter({ ...filter, filter: f })}
            filterFields={[
              {
                field: 'name',
                label: 'Navn',
                type: 'string',
              },
              {
                field: 'vat_number',
                label: 'VAT nr.',
                type: 'string',
              },
              {
                field: 'createdAt',
                label: 'Oprettet',
                type: 'date',
              },
              {
                field: 'paymentLikelihood',
                label: 'Betalingssikkerhed',
                type: 'option',
                options: [
                  {
                    id: PaymentLikelihoodType.ALWAYS,
                    label: 'Altid',
                  },
                  {
                    id: PaymentLikelihoodType.HIGH,
                    label: 'Høj',
                  },
                  {
                    id: PaymentLikelihoodType.MEDIUM,
                    label: 'Medium',
                  },
                  {
                    id: PaymentLikelihoodType.MEDIUM,
                    label: 'Lav',
                  },
                ],
              },
              {
                field: 'invoicedTotal',
                label: 'Faktureret',
                type: 'number',
              },
              {
                field: 'tags',
                label: 'Tags',
                type: 'tag',
              },
            ]}
          />

          <Button
            marginLeft="auto"
            iconLeft={<Icon icon="add" />}
            onClick={() => setIsCreatingCompany(true)}
          >
            Opret virksomhed
          </Button>
        </Box>
      </Container>

      <VirtualTable
        isLoading={isLoading}
        items={items}
        total={total}
        rowHeight={48}
        header={[
          { title: 'Navn', orderByKeys: ['name', 'id'] },
          {
            title: 'Nummer',
            width: 140,
            orderByKeys: ['company_number', 'id'],
          },
          { title: 'VAT nr.', width: 140, orderByKeys: ['vat_number', 'id'] },
          { title: 'Domæner', orderByKeys: ['domains__name', 'id'] },
          { title: 'Oprettet', width: 180, orderByKeys: ['created_at', 'id'] },
          {
            title: 'Betalingssikkerhed',
            orderByKeys: ['payment_likelihood', 'id'],
          },
          {
            title: 'Faktureret',
            align: 'right',
            width: 180,
            orderByKeys: ['invoiced_amount', 'id'],
          },
          { title: 'Ansvarlig', orderByKeys: ['responsible', 'id'] },
          { title: 'Tags', orderByKeys: ['tags__name', 'id'] },
        ]}
        onRequestMore={async () => fetchMore()}
        ordering={filter.ordering}
        onOrderingChange={(ordering) =>
          setFilter({ filter: filter.filter, ordering })
        }
        renderRow={({ item: company, index }) => (
          <MemoizedRow company={company} />
        )}
        noItemsPlaceholder={
          <Box width="100%" textAlign="center" paddingY={majorScale(6)}>
            <Heading size={500} marginBottom={majorScale(1)}>
              <FormattedMessage defaultMessage="No matching companies found" />
            </Heading>
            <p>
              <FormattedMessage
                defaultMessage="Try changing your filters or <link>see all your companies</link>"
                values={{
                  link: (chunk: string) => (
                    <span
                      className="link medium"
                      onClick={() =>
                        setFilter({ filter: {}, ordering: filter.ordering })
                      }
                    >
                      {chunk}
                    </span>
                  ),
                }}
              />
            </p>
          </Box>
        }
      />
    </PageContainer>
  );
};

const MemoizedRow = memo(
  observer(({ company }: { company: ICompany }) => (
    <>
      <VirtualTableItem>
        <CompanyThumbnail company={company} />
      </VirtualTableItem>
      <VirtualTableItem className="font-mono">
        {company.company_number}
      </VirtualTableItem>
      <VirtualTableItem className="font-mono">
        {company.vat_number}
      </VirtualTableItem>
      <VirtualTableItem>
        {company.domains.map((d) => d.domain).join(', ')}
      </VirtualTableItem>
      <VirtualTableItem>
        <FormattedDate value={company.created_at} format="default" />
      </VirtualTableItem>
      <VirtualTableItem>
        {company.payment_likelihood && (
          <PaymentLikelihoodTag
            paymentLikelihood={company.payment_likelihood}
            showDate
          />
        )}
      </VirtualTableItem>
      <VirtualTableItem justifyContent="flex-end" className="font-mono">
        <FormattedNumber
          // eslint-disable-next-line react/style-prop-object
          style="currency"
          currency={company.currency}
          value={company.invoiced_amount}
          currencyDisplay="code"
        />
      </VirtualTableItem>
      <VirtualTableItem>
        {company.responsible.length > 0 &&
        company.responsible.reduce((p, n) => !!p && !!n, true)
          ? company.responsible.map((member) => member!.user.name).join(', ')
          : ''}
      </VirtualTableItem>
      <VirtualTableItem>
        {company.tags.map((t) => (
          <Tag key={t} small={true} marginRight={minorScale(1)} intent="info">
            {t}
          </Tag>
        ))}
      </VirtualTableItem>
    </>
  ))
);
